import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { modelStrategyApi } from '@/api/modelStrategy';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountId } from '@/redux/session/selectors';
import { ModelCatalog, ModelCatalogEntry, ModelStrategyStep } from '@/shared/types/modelStrategy';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

const CATALOG_STALE_MS = 60_000;

export type CatalogBuckets = {
  internal: ModelCatalogEntry[];
  proxy: ModelCatalogEntry[];
  external: ModelCatalogEntry[];
};

/**
 * Resolve the catalog entry a step points at. A both-null step is the Moveo
 * default, which maps to the first internal entry.
 */
export const findCatalogEntry = (
  buckets: CatalogBuckets,
  step: Pick<ModelStrategyStep, 'language_model_id' | 'language_model_alias'>
): ModelCatalogEntry | undefined => {
  if (step.language_model_id) {
    return buckets.external.find((e) => e.language_model_id === step.language_model_id);
  }
  if (step.language_model_alias) {
    return [...buckets.internal, ...buckets.proxy].find(
      (e) => e.alias === step.language_model_alias
    );
  }
  return buckets.internal[0];
};

/**
 * Brain-scoped model catalog. Returns both the raw list and a bucket-grouped
 * view tailored for the picker. Buckets default to empty / false so callers
 * can render skeletons without null guards.
 */
export const useModelStrategyCatalog = (brainId: string | undefined) => {
  const accountId = useSelector(selectAccountId);
  const canRead = useSelector((state: RootState) =>
    getPermissions(state, 'model_strategy', actions.READ)
  );

  const { data, status } = useQuery<ModelCatalog>({
    queryKey: ['brains', brainId ?? '-', 'model-strategy', 'catalog'],
    queryFn: () => modelStrategyApi.catalog(brainId!),
    enabled: !!accountId && !!brainId && canRead,
    staleTime: CATALOG_STALE_MS,
  });

  const buckets = useMemo<CatalogBuckets>(() => {
    const empty: CatalogBuckets = { internal: [], proxy: [], external: [] };
    if (!data) {
      return empty;
    }
    return data.models.reduce<CatalogBuckets>((acc, entry) => {
      acc[entry.bucket].push(entry);
      return acc;
    }, empty);
  }, [data]);

  return { models: data?.models ?? [], buckets, status };
};
